import _ from 'lodash';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import styled, { css } from 'styled-components';

import AgentPerformanceTable from './report/lib/agent_performance_table';
import BasicAgentReport from './report/agent_report';
import DownloadIcon from './icons/download_icon.jsx';
import EmbeddedReport from './report/embedded_report';
import { findReportConfigBySlug, getSlugs } from 'models/reports/report';
import FullPageSpinner from 'components/common/full_page_spinner';
import { getAllReportConfigs } from 'scripts/application/actions/reporting/lib/reporting_helpers';
import IvrEndStateReport from './report/ivr_end_state_report';
import IvrReportFiltersContainer from './ivr_report_filters';
import IvrSummaryReportContainer from './report/ivr_summary_report_container';
import { isEmbeddedReport } from 'models/reports/report';
import Link from 'components/common/link';
import NavigateTo from 'actions/current_location/navigate_to';
import NavigateToUrl from 'actions/current_location/navigate_to_url';
import PageLayout from '../page_layout';
import ReportChannel from 'models/reports/report_channel';
import ReportFilters from './report_filters';
import ReportConfigHeader from './report_config_header';
import ReportNavigation from 'components/reporting/report_navigation';
import ReportZeroState from './report_zero_state';
import Reports from 'models/location/reports';
import ShowToast from 'actions/toast/show_toast';
import SummaryReport from './report/summary_report';
import TimezoneSelector from './timezone_selector';
import TopicReport from './report/topic_report';
import TrackEvent from 'actions/analytics/track_event';
import TwoArrowsRight from 'components/lib/icons/two_arrows_right';
import TwoArrowsLeft from 'components/lib/icons/two_arrows_left';
import WindowSizeWatcher from 'scripts/presentation/components/common/utilities/window_size_watcher';
import connect from 'components/lib/connect';
import { MenuContainer, MenuItem } from 'components/lib/menu_deprecated';
import { ToastType } from 'models/toast';
import { getLocationUrl } from 'scripts/adapters/routes/location_url';
import { getUrl } from 'scripts/adapters/routes/url';

export const IVR_TAB_OPTION = 'ivr';
const MENU_WIDTH = 310;

class ReportHome extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isDownloadButtonDisabled: false,
      isEmbeddedReportLoading: false,
      isReportNavigationOpen: !this.isLoadingOrRestricted(),
      // don't render the navigation when it's offscreen, but do render it during the transition animations
      isReportNavigationHidden: this.isLoadingOrRestricted(),
    };

    this.getReportLocation = this.getReportLocation.bind(this);
    this.getReportUrl = this.getReportUrl.bind(this);
    this.toggleReportNavigationMenu = this.toggleReportNavigationMenu.bind(this);
    this.closeReportNavigationMenu = this.closeReportNavigationMenu.bind(this);
    this.onReportSelect = this.onReportSelect.bind(this);
    this.renderGlobalReportControls = this.renderGlobalReportControls.bind(this);
    this.setEmbeddedReportLoading = this.setEmbeddedReportLoading.bind(this);
    this.resetEmbeddedReportLoading = this.resetEmbeddedReportLoading.bind(this);
  }

  setEmbeddedReportLoading() {
    this.setState({
      isEmbeddedReportLoading: true,
      isReportNavigationOpen: false,
    });
  }

  resetEmbeddedReportLoading() {
    this.setState({ isEmbeddedReportLoading: false });
  }

  /* Render */
  render() {
    const { filterParams, isIvrReport, reportConfigs, reportSlug, shouldLoadZeroState } = this.props;
    const currentReportConfig = findReportConfigBySlug(reportConfigs, reportSlug);
    return (
      <PageLayout id="reports" className="reportHome">
        <ReportHomeFlexWrapper>
          <StyledSidebar
            isReportNavOpen={this.state.isReportNavigationOpen}
            onTransitionEnd={() => this.setState({ isReportNavigationHidden: !this.state.isReportNavigationOpen })}
          >
            {this.renderSidebarNavigationTab()}
            {!this.state.isReportNavigationHidden ? this.renderSidebarNavigation(reportConfigs) : null}
          </StyledSidebar>
          <InvisiblePlaceholder isReportNavOpen={this.state.isReportNavigationOpen} />
          <StyledReportMain isReportNavOpen={this.state.isReportNavigationOpen}>
            <ReportConfigHeader
              config={currentReportConfig}
              renderSecondaryElements={this.renderGlobalReportControls}
            />
            {isIvrReport ? this.renderIvrSubTabs() : null}
            {isIvrReport ? (
              <IvrReportFiltersContainer
                isApplyForceEnabled={shouldLoadZeroState}
                closeReportNavigationMenu={this.closeReportNavigationMenu}
              />
            ) : (
              <ReportFilters
                closeReportNavigationMenu={this.closeReportNavigationMenu}
                config={currentReportConfig}
                filterParams={filterParams}
                key={reportSlug}
                isDisabled={this.isLoadingOrRestricted()}
                isApplyForceEnabled={shouldLoadZeroState}
              />
            )}
            {shouldLoadZeroState ? (
              <ReportZeroState />
            ) : (
              <WindowSizeWatcher>{({ windowWidth }) => this.renderReport(reportSlug, windowWidth)}</WindowSizeWatcher>
            )}
          </StyledReportMain>
        </ReportHomeFlexWrapper>
      </PageLayout>
    );
  }

  renderIvrSubTabs() {
    const { navigateToUrl, reportSlug } = this.props;

    let ivrSummaryUrl = this.getReportUrl('ivr-summary');
    let ivrEndStatesUrl = this.getReportUrl('ivr-end-states');

    return (
      <div className="reportHeader reportHeader-tabs">
        <Link
          className={classnames('reportHeader-tab', {
            'reportHeader-activeTab': reportSlug === 'ivr-summary',
          })}
          onClick={navigateToUrl.bind(this, ivrSummaryUrl)}
          href={ivrSummaryUrl}
        >
          Executive Summary
        </Link>
        <Link
          className={classnames('reportHeader-tab', {
            'reportHeader-activeTab': reportSlug === 'ivr-end-states',
          })}
          onClick={navigateToUrl.bind(this, ivrEndStatesUrl)}
          href={ivrEndStatesUrl}
        >
          End States
        </Link>
      </div>
    );
  }

  renderGlobalReportControls() {
    if (!this.isEmbeddedReport()) {
      return (
        <div className="reportHeader-info-secondaryElements">
          <TimezoneSelector />
          {this.renderDownloadButton()}
        </div>
      );
    }
  }

  renderDownloadButton() {
    const { auth } = this.props;
    if (auth.isLoggedIn()) {
      // only render if we can determine their orgId
      let orgId = auth.getOrgId();
      let reportSlug = this.props.reportSlug;
      let filename = `${reportSlug}.csv`;
      let baseUrl = `/api/reporting/v1/orgs/${orgId}/reports/${filename}`;
      return this.renderDownloadMenu({ baseUrl, filename });
    }
  }

  renderSidebarNavigation(reportConfigs) {
    return (
      <NavigationWrapper>
        <ReportNavigation
          activeReport={this.props.reportSlug}
          // TODO: Remove isCollapsible prop below when number of reports warrants collapsible categories (the default
          // for prop is already set to true in ReportNavigation). See: ch57683
          isCollapsible={false}
          isIvrReportingEnabled={this.props.isIvrReportingEnabled}
          onReportSelect={this.onReportSelect}
          reportConfigs={reportConfigs}
        />
      </NavigationWrapper>
    );
  }

  renderSidebarNavigationTab() {
    return (
      <div>
        <CollapseTab
          data-aid={'reportHome-sideNav-collapseTab'}
          isReportNavOpen={this.state.isReportNavigationOpen}
          onClick={this.toggleReportNavigationMenu}
        >
          <CollapseArrows />
        </CollapseTab>
        <ExpandTab
          data-aid={'reportHome-sideNav-expandTab'}
          isReportNavOpen={this.state.isReportNavigationOpen}
          onClick={this.toggleReportNavigationMenu}
          isReportNavDisabled={this.isLoadingOrRestricted()}
        >
          <ExpandArrows isReportNavDisabled={this.isLoadingOrRestricted()} />
        </ExpandTab>
      </div>
    );
  }

  renderDownloadMenu({ baseUrl, filename }) {
    const {
      isIvrReport,
      filterParams,
      currentLocation,
      reportSlug,
      shouldLoadZeroState,
      showInfoToast,
      trackEvent,
    } = this.props;

    const timezone = currentLocation.timezone;
    filterParams.timezone = timezone;
    let dailyUrl = getUrl(baseUrl, _.omit(filterParams, 'aggregationLevel'));
    let halfHourlyUrl = getUrl(baseUrl, {
      ...filterParams,
      aggregationLevel: 'HALF_HOUR',
    });

    const isDefaultIvrReport = isIvrReport && !filterParams.endpointNumber;

    let isDownloadDisabled = isDefaultIvrReport || shouldLoadZeroState || this.state.isDownloadButtonDisabled;

    const menuClassName = isDefaultIvrReport
      ? 'reportHome-header-downloadMenu-disabled'
      : 'reportHome-header-downloadMenu';

    const buttonClassName = isDownloadDisabled
      ? 'reportHome-header-downloadMenu-button-content-disabled'
      : 'reportHome-header-downloadMenu-button-content';

    let reportParams = { slug: reportSlug, view: 'CSV', timezone, ..._.omit(filterParams, 'aggregationLevel') };

    const menuOptions = isIvrReport
      ? [{ name: 'Daily', url: dailyUrl, params: { ...reportParams, aggregationLevel: 'DAY' } }]
      : [
          {
            name: 'Half hourly',
            url: halfHourlyUrl,
            params: { ...reportParams, aggregationLevel: 'HALF_HOUR' },
          },
          { name: 'Daily', url: dailyUrl, params: { ...reportParams, aggregationLevel: 'DAY' } },
        ];
    let brieflyDisableDownloadButton = () => {
      showInfoToast();
      this.setState({ isDownloadButtonDisabled: true });
      setTimeout(() => {
        this.setState({ isDownloadButtonDisabled: false });
      }, 4000);
    };

    let disableAndTrack = arg => {
      trackEvent(arg);
      brieflyDisableDownloadButton();
    };

    disableAndTrack.bind(this);

    let menuItems = menuOptions.map((option, index) => (
      <a className="reportHome-header-downloadMenu-item" href={option.url} download={filename} key={index}>
        <ReportHomeMenuItemSelector
          onSelect={disableAndTrack}
          params={option.params}
          className={`reportHome-header-downloadMenu-item-${_.camelCase(option.name)}`}
          name={option.name}
        />
      </a>
    ));

    return (
      <MenuContainer
        className={menuClassName}
        ref="menu"
        isDisabled={isDownloadDisabled}
        button={
          <div className={buttonClassName}>
            <DownloadIcon />
            Download CSV
          </div>
        }
      >
        {menuItems}
      </MenuContainer>
    );
  }

  toggleReportNavigationMenu(evt) {
    evt.preventDefault();
    const newNavOpenState = !this.state.isReportNavigationOpen;
    const newState = {
      isReportNavigationOpen: newNavOpenState,
    };

    // If the navigation is opening, we want to unhide it before the open animiation.
    // We do NOT want to set `isReportNavigationHidden` to `true` if the navigation is closing because
    // we don't want to unmount it before the close animation - the onTransitionEnd handler will take care of that.
    if (newNavOpenState) {
      newState.isReportNavigationHidden = false;
    }

    this.setState(newState);
  }

  closeReportNavigationMenu(evt) {
    this.setState({
      isReportNavigationOpen: false,
    });
  }

  getReportLocation(slug) {
    if (slug === IVR_TAB_OPTION) {
      // use ivr-summary for default IVR report
      slug = 'ivr-summary';
    }
    let currentLocation = this.props.currentLocation;
    return Reports.create({ ...currentLocation, slug });
  }

  getReportUrl(slug) {
    let currentLocation = this.props.currentLocation;
    if (slug === IVR_TAB_OPTION) {
      // use ivr-summary for default IVR report
      slug = 'ivr-summary';
    }
    return getLocationUrl(Reports.create({ ...currentLocation, slug }));
  }

  onReportSelect(reportSlug) {
    const reportLocation = this.getReportLocation(reportSlug);
    this.props.navigateTo(reportLocation);
  }

  renderReport(type, windowWidth) {
    if (this.isEmbeddedReport()) {
      return (
        <React.Fragment>
          {this.isLoadingOrRestricted() && (
            <FullPageSpinner className="reportHome-loading">Loading your report</FullPageSpinner>
          )}
          {/* Always render because we need to wait for EmbeddedReport's iframe to load. Hide until that iframe is loaded. */}
          <div
            className={classnames('reportHome-embeddedReport-wrapper', {
              'reportHome-hidden': this.isLoadingOrRestricted(),
            })}
          >
            <EmbeddedReport
              embeddedReport={this.props.embeddedReport.get()}
              setEmbeddedReportLoading={this.setEmbeddedReportLoading}
              resetEmbeddedReportLoading={this.resetEmbeddedReportLoading}
            />
          </div>
        </React.Fragment>
      );
    }

    const { filterParams, reportChannel } = this.props;
    if (this.isLoadingOrRestricted()) {
      return <FullPageSpinner className="reportHome-loading">Loading your report</FullPageSpinner>;
    }

    let report = this.props.report.get();
    if (type === 'summary') {
      return <SummaryReport report={report} windowWidth={windowWidth} />;
    } else if (type === 'agents') {
      return (
        <AgentReport
          filterParams={filterParams}
          report={report}
          reportChannel={reportChannel}
          windowWidth={windowWidth}
        />
      );
    } else if (type === 'topics') {
      return <TopicReport report={report} windowWidth={windowWidth} {...filterParams} />;
    } else if (type === 'ivr-summary') {
      return <IvrSummaryReportContainer report={report} windowWidth={windowWidth} />;
    } else if (type === 'ivr-end-states') {
      return <IvrEndStateReport report={report} windowWidth={windowWidth} />;
    }
    return null;
  }

  isEmbeddedReport() {
    return isEmbeddedReport(this.props.reportSlug);
  }

  isLoadingOrRestricted() {
    const { embeddedReport, isViewReportsEnabled, report } = this.props;
    if (this.isEmbeddedReport()) {
      return (
        // checks this.state because this function gets called in the constructor
        embeddedReport.isLoading() || !isViewReportsEnabled || (this.state ? this.state.isEmbeddedReportLoading : false)
      );
    } else {
      return report.isLoading() || !isViewReportsEnabled;
    }
  }
}

class ReportHomeMenuItemSelector extends React.Component {
  constructor(props) {
    super(props);

    this.onSelect = this.props.onSelect.bind(this, {
      event: 'Report Requested',
      props: this.props.params,
    });
  }

  render() {
    return (
      <MenuItem onSelect={this.onSelect} className={this.props.className}>
        {this.props.name}
      </MenuItem>
    );
  }
}

ReportHome.propTypes = {
  auth: PropTypes.object,
  currentLocation: PropTypes.object,
  embeddedReport: PropTypes.object,
  filterParams: PropTypes.object,
  isIvrReport: PropTypes.bool,
  isIvrReportingEnabled: PropTypes.bool,
  isViewReportsEnabled: PropTypes.bool,
  report: PropTypes.object,
  reportChannel: PropTypes.string,
  reportConfigs: PropTypes.array,
  reportSelectorOptions: PropTypes.arrayOf(PropTypes.string),
  reportSlug: PropTypes.string,
  shouldLoadZeroState: PropTypes.bool,

  navigateTo: PropTypes.func,
  navigateToUrl: PropTypes.func,
  showInfoToast: PropTypes.func,
  trackEvent: PropTypes.func,
};

function AgentReport({ filterParams, report, reportChannel, windowWidth }) {
  if ((reportChannel === ReportChannel.ALL || !reportChannel) && filterParams.routingGroupIds.length === 0) {
    return <AgentPerformanceTable report={report} />;
  }

  return <BasicAgentReport report={report} windowWidth={windowWidth} channel={reportChannel} />;
}

AgentReport.propTypes = {
  filterParams: PropTypes.object,
  report: PropTypes.object,
  reportChannel: PropTypes.string,
  windowWidth: PropTypes.number,
};

function mapStateToProps({ getProvider, isFeatureEnabled }) {
  const currentLocation = getProvider('currentLocation').get();
  const auth = getProvider('auth').get();
  const report = getProvider('report');
  const embeddedReport = getProvider('embeddedReport');

  const reportConfigs = getAllReportConfigs({ stores: { reportConfigs: getProvider('reportConfigs') } });

  const isIvrReportingEnabled = isFeatureEnabled('ivrReporting');
  const isViewReportsEnabled = isFeatureEnabled('viewReports');

  const reportSlug = currentLocation.getSlug();
  const reportChannel = currentLocation.getChannel();

  const filterParams = {
    routingGroupIds: currentLocation.getRoutingGroupIds(),
    teamIds: currentLocation.getTeamIds(),
    topicIds: currentLocation.getTopicIds(),
    startAt: currentLocation.getStartAt(),
    endAt: currentLocation.getEndAt(),
    channel: reportChannel,
    aggregationLevel: currentLocation.getAggregationLevel(
      findReportConfigBySlug(reportConfigs, reportSlug).timeAggregation
    ),
    endpointNumber: currentLocation.getEndpointNumber(),
  };

  const isIvrReport = reportSlug === 'ivr-summary' || reportSlug === 'ivr-end-states';

  const shouldLoadZeroState =
    !currentLocation.reportFetched() ||
    (currentLocation.isEmbeddedReport()
      ? !embeddedReport.get()
      : !((report.get() || report.isLoading()) && (isIvrReport || currentLocation.hasFilters())));

  const reportSelectorOptions = Object.freeze(
    _.uniq([
      'summary',
      'agents',
      'topics',
      ...(isIvrReportingEnabled ? [IVR_TAB_OPTION] : []),
      ..._.values(getSlugs(reportConfigs)),
    ])
  );

  return {
    auth,
    currentLocation,
    embeddedReport,
    filterParams,
    isIvrReport,
    isIvrReportingEnabled,
    isViewReportsEnabled,
    report,
    reportChannel,
    reportConfigs,
    reportSlug,
    shouldLoadZeroState,
    reportSelectorOptions,
  };
}

function mapExecuteToProps(executeAction) {
  return {
    navigateTo: location => executeAction(NavigateTo, location),
    navigateToUrl: url => executeAction(NavigateToUrl, url),
    trackEvent: arg => executeAction(TrackEvent, arg),
    showInfoToast: () =>
      executeAction(ShowToast, {
        type: ToastType.INFO,
        message: 'Hold tight - we are preparing your report for download.',
      }),
  };
}

const ReportHomeContainer = connect(mapStateToProps, mapExecuteToProps)(ReportHome);

export default ReportHomeContainer;

const ReportHomeFlexWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
`;

// InvisiblePlaceholder grows and shrinks with the nav menu
// so the reports get compressed to the right
const InvisiblePlaceholder = styled.div`
  display: inline-block;
  height: 100vh;
  left: 0px;
  position: relative;
  top: 0px;
  transition: width 0.5s ease;
  visibility: hidden;
  width: 0px;

  ${p => p.isReportNavOpen && grow};
`;

const StyledSidebar = styled.div`
  height: 100vh;
  position: fixed;
  transition: transform 0.5s ease;
  z-index: 6;

  ${p => p.isReportNavOpen && slideRight};
  ${p => !p.isReportNavOpen && slideCenter};
`;

const NavigationWrapper = styled.div`
  background-color: ${p => p.theme.colors.gray200};
  box-shadow: inset -3px 3px 3px ${p => p.theme.colors.gray300};
  display: inline-block;
  height: 94%;
  left: -${MENU_WIDTH}px;
  overflow: auto;
  padding: 32px;
  position: absolute;
  top: 0;
  width: ${MENU_WIDTH}px;
`;

const TabBase = css`
  align-items: center;
  background-color: ${p => p.theme.colors.white};
  border: 1px solid ${p => p.theme.colors.gray300};
  cursor: pointer;
  display: flex;
  height: 28px;
  position: absolute;
  top: 22px;
  transition: opacity 0.5s ease, visibility 0.5s ease;
  width: 28px;
  z-index: 9;

  &:hover {
    background: ${p => p.theme.colors.gray100};
  }
`;

const ExpandTab = styled.div`
  ${TabBase};

  border-left: none;
  border-radius: 0px 8px 8px 0px;
  box-shadow: 2px 2px 8px ${p => p.theme.colors.gray300};

  &:hover {
    background-color: ${p => p.theme.colors.gray100};
  }

  ${p => p.isReportNavOpen && hideExpand};
  ${p => p.isReportNavDisabled && disableExpandTab};
`;

const CollapseTab = styled.div`
  ${TabBase};

  border-radius: 8px 0px 0px 8px;
  border-right: none;
  box-shadow: -2px 2px 8px ${p => p.theme.colors.gray300};
  right: 0px;

  &:hover {
    background-color: ${p => p.theme.colors.gray100};
  }

  ${p => !p.isReportNavOpen && slideCenter && hideCollapse};
`;

const ArrowBase = css`
  height: 14px;
  padding-left: 6px;
`;

const ExpandArrows = styled(TwoArrowsRight)`
  ${ArrowBase};

  ${p => p.isReportNavDisabled && disableExpandArrows};
`;

const CollapseArrows = styled(TwoArrowsLeft)`
  ${ArrowBase};
`;

const StyledReportMain = styled.div`
  display: inline-block;
  width: 100%;
`;

const hideExpand = css`
  opacity: 0;
  visibility: hidden;
`;

const disableExpandTab = css`
  box-shadow: none;
  pointer-events: none;
`;

const disableExpandArrows = css`
  filter: brightness(150%);
`;

const hideCollapse = css`
  opacity: 0;
`;

const grow = css`
  width: 400px;
`;

const slideRight = css`
  transform: translateX(310px);
`;

const slideCenter = css`
  transform: translateX(0px);
`;



// WEBPACK FOOTER //
// ./src/scripts/presentation/components/reporting/report_home.jsx